import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { mockDocuments } from "@/lib/mock-data";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import {
	ArrowRight,
	BarChart3,
	BookMarked,
	Cpu,
	Database,
	FileText,
	LayoutDashboard,
	Library,
	Lightbulb,
	ListChecks,
	MessageCircleQuestion,
	Network,
	Search,
	Settings,
	Sparkles,
	Upload,
	Video,
} from "lucide-react";
import { useCallback, useState } from "react";

interface CommandPaletteProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const pages = [
	{ name: "Dashboard", path: "/", icon: LayoutDashboard },
	{ name: "Review", path: "/review", icon: Sparkles },
	{ name: "Ingestion", path: "/ingest", icon: Upload },
	{ name: "Video Queue", path: "/video-queue", icon: Video },
	{ name: "Book Queue", path: "/book-queue", icon: BookMarked },
	{ name: "Search", path: "/search", icon: Search },
	{ name: "Ask My Brain", path: "/ask", icon: MessageCircleQuestion },
	{ name: "Knowledge Graph", path: "/graph", icon: Network },
	{ name: "Vector KB", path: "/vector-kb", icon: Database },
	{ name: "Documents", path: "/documents", icon: Library },
	{ name: "Gap Queue", path: "/gap-queue", icon: ListChecks },
	{ name: "Synthesis", path: "/synthesis", icon: Lightbulb },
	{ name: "Statistics", path: "/stats", icon: BarChart3 },
	{ name: "Settings", path: "/settings", icon: Settings },
	{ name: "AI Models", path: "/ai-models", icon: Cpu },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
	const navigate = useNavigate();
	const [search, setSearch] = useState("");

	const handleSelect = useCallback(
		(path: string) => {
			onOpenChange(false);
			setSearch("");
			navigate({ to: path });
		},
		[navigate, onOpenChange],
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="overflow-hidden p-0 max-w-[520px] bg-card/95 backdrop-blur-xl border-border/60 shadow-2xl">
				<DialogTitle className="sr-only">Command palette</DialogTitle>
				<Command
					className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.12em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted-foreground/60"
					loop
				>
					<div className="flex items-center border-b border-border/50 px-4">
						<Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
						<Command.Input
							value={search}
							onValueChange={setSearch}
							placeholder="Search pages, documents, concepts..."
							className="flex h-12 w-full bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
						/>
					</div>
					<Command.List className="max-h-[320px] overflow-y-auto p-2">
						<Command.Empty className="py-8 text-center text-sm text-muted-foreground">
							No results found.
						</Command.Empty>

						<Command.Group heading="Pages">
							{pages.map((page) => (
								<Command.Item
									key={page.path}
									value={page.name}
									onSelect={() => handleSelect(page.path)}
									className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground cursor-pointer transition-colors aria-selected:bg-primary/8 aria-selected:text-foreground"
								>
									<page.icon size={16} className="shrink-0" />
									<span className="flex-1">{page.name}</span>
									<ArrowRight
										size={12}
										className="opacity-0 group-aria-selected:opacity-100 transition-opacity"
									/>
								</Command.Item>
							))}
						</Command.Group>

						{search.length > 0 && (
							<Command.Group heading="Documents">
								{mockDocuments
									.filter((d) => d.title.toLowerCase().includes(search.toLowerCase()))
									.slice(0, 5)
									.map((doc) => (
										<Command.Item
											key={doc.id}
											value={doc.title}
											onSelect={() => handleSelect(`/documents/${doc.id}`)}
											className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground cursor-pointer transition-colors aria-selected:bg-primary/8 aria-selected:text-foreground"
										>
											<FileText size={16} className="shrink-0" />
											<div className="flex flex-col min-w-0 flex-1">
												<span className="truncate">{doc.title}</span>
												<span className="text-[11px] text-muted-foreground/60">
													{doc.author} &middot; {doc.category}
												</span>
											</div>
										</Command.Item>
									))}
							</Command.Group>
						)}
					</Command.List>

					<div className="flex items-center justify-between border-t border-border/50 px-4 py-2 text-[11px] text-muted-foreground/50">
						<div className="flex items-center gap-3">
							<span>
								<kbd className="font-mono">↑↓</kbd> navigate
							</span>
							<span>
								<kbd className="font-mono">↵</kbd> select
							</span>
							<span>
								<kbd className="font-mono">esc</kbd> close
							</span>
						</div>
					</div>
				</Command>
			</DialogContent>
		</Dialog>
	);
}
